import { BUTTON_HEIGHT, Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { Page } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import { downscaleProportionally } from 'lib/scaleWidthHeight';
import { theme } from 'lib/theme';
import React from 'react';
import {
  LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doClosePlans,
  doPlanUnsetPin,
  doPlansLoadPlan,
  doPlansSetSelectedPinId,
  doPlansUpdatePin,
  doPlansUsePin,
} from 'store/plan/actions';
import {
  selectAfterActionFind,
  selectPlansFindNoun,
  selectPlansPlan,
  selectPlansPinIsDirty,
  selectPlansSelectedPinId,
  selectSelectionKey,
  selectPlansPinPlanId,
} from 'store/plan/selectors';

import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { str } from 'lib/str';
import { useEsc } from 'hooks/useEsc';
import { ViewZoom, ViewZoomRef } from 'components/ViewZoom';
import { PIN_DIM } from './Pin';
import { PinsMain, PinsRef } from './PinsMain';
import { PinsOther } from './PinsOther';
import { CanvasImage, CanvasImageRef } from './CanvasImage';

export const Canvas: React.FC<ModalScreenProps<ModalScreens.PLANS>> = () => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const isDirtyRef = React.useRef(false);
  const [canvasDims, setCanvasDims] = React.useState({ height: 0, width: 0 });
  const pinsRef = React.useRef<PinsRef>(null);
  const pinsOtherRef = React.useRef<PinsRef>(null);
  const zoomableViewRef = React.useRef<ViewZoomRef>(null);
  const canvasImageRef = React.useRef<CanvasImageRef>(null);

  const zoomScale = React.useRef(1);

  const selectedIndex = useSelector(selectPlansSelectedPinId);
  const pinDirty = useSelector(selectPlansPinIsDirty);

  const plan = useSelector(selectPlansPlan);
  const pinPlanId = useSelector(selectPlansPinPlanId);
  const pinInPlan = pinPlanId && pinPlanId === plan?.id;

  const selectionKey = useSelector(selectSelectionKey);
  const afterActionFind = useSelector(selectAfterActionFind);
  const findNoun = useSelector(selectPlansFindNoun);

  const isCreate = !!selectionKey;
  const isCreateMode = isCreate && !pinInPlan;
  const showPinAddedText = isCreate && !!pinInPlan;
  const showGoToPlanId = isCreate && !pinInPlan ? pinPlanId : undefined;
  const showRemovePinBtn = isCreate && !!pinPlanId;

  const isFind = !!afterActionFind;
  const isFoundMode = isFind && selectedIndex !== undefined;

  const image = plan?.image || {
    base64: '',
    url: '',
    height: 0,
    width: 0,
    type: 'image',
    page: 0,
    isUsingThumbnail: false,
    isFileMissing: true,
  };

  const canvasPadding = PIN_DIM.height + theme.paddingHorizontal;

  const downscaledImageSize = downscaleProportionally(
    image.width,
    image.height,
    canvasDims.width - canvasPadding * 2,
    canvasDims.height - canvasPadding * 2,
  );

  const calculatedSize = {
    canvasHeight: canvasDims.height,
    canvasWidth: canvasDims.width,
    imageHeight: downscaledImageSize.height,
    imageWidth: downscaledImageSize.width,
  };

  const handleOnLayout = React.useCallback(
    ({
      nativeEvent: {
        layout: { width, height },
      },
    }: LayoutChangeEvent) =>
      setCanvasDims({
        height,
        width,
      }),
    [],
  );

  const handlePinMove = React.useCallback(
    ({ x, y }: { x: number; y: number }) => {
      isDirtyRef.current = true;
      dispatch(
        doPlansUpdatePin({
          name: '',
          planId: plan?.id || '',
          x: +x.toFixed(12),
          y: +y.toFixed(12),
        }),
      );
    },
    [dispatch, plan?.id],
  );

  const handlePinPress = React.useCallback(
    (id: string) => {
      dispatch(doPlansSetSelectedPinId(id));
    },
    [dispatch],
  );

  const handleMainPinPress = React.useCallback(() => {
    zoomableViewRef.current?.setEnabled(false);
  }, [zoomableViewRef]);

  const handleMainPinPressUp = React.useCallback(() => {
    zoomableViewRef.current?.setEnabled(true);
  }, [zoomableViewRef]);

  const handleAddPin = ({ x, y }: { x: number; y: number }) => {
    if (isCreateMode) {
      dispatch(
        doPlansUpdatePin({
          name: '',
          planId: plan?.id || '',
          x: +((x / calculatedSize.imageWidth) * 100).toFixed(12),
          y: +((y / calculatedSize.imageHeight) * 100).toFixed(12),
        }),
      );
    }
  };

  const handleCanvasPan = React.useCallback(
    ({ x, y }: { x: number; y: number }) => {
      pinsRef.current?.panChange({ x, y });
      pinsOtherRef.current?.panChange({ x, y });
    },
    [pinsRef, pinsOtherRef],
  );

  const handleCanvasZoomChange = React.useCallback(
    ({ scale }: { scale: number }) => {
      zoomScale.current = scale;
      if (image.type === 'pdf' || image.type === 'pdfpage') {
        canvasImageRef.current?.setPdfScale(scale);
      }
      pinsRef.current?.scaleChange(scale);
      pinsOtherRef.current?.scaleChange(scale);
    },
    [canvasImageRef, image.type, pinsOtherRef, pinsRef],
  );

  const handleZoomIn = () => {
    zoomableViewRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    zoomableViewRef.current?.zoomOut();
  };

  const handleZoomReset = () => {
    zoomableViewRef.current?.zoomReset();
  };

  const handleUseSelectedPinPress = React.useCallback(() => {
    dispatch(doPlansUsePin());
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    if (pinDirty) {
      confirmUnsaved({
        onSave: handleUseSelectedPinPress,
        onDiscard: () => dispatch(doClosePlans()),
      });
      return;
    }
    dispatch(doClosePlans());
  }, [confirmUnsaved, dispatch, handleUseSelectedPinPress, pinDirty]);

  useEsc(handleClose);

  if (!plan) {
    return null;
  }

  return (
    <Page
      isInModal={Platform.OS !== 'ios'}
      contentPaddingBottom={0}
      contentPaddingTop={0}
      showFloatingHeader={false}
      header={
        isCreate ? (
          <NavigationHeader
            heading={plan.name}
            subheading={showPinAddedText ? 'Pin Added' : 'Tap to Add Pin'}
            onPressCancelButton={handleClose}
            onPressSaveButton={handleUseSelectedPinPress}
            saveButtonText="Done"
            cancelButtonText="Close"
          />
        ) : (
          <NavigationHeader
            heading={plan.name}
            subheading={findNoun ? `Find ${findNoun}` : undefined}
            onPressCancelButton={handleClose}
            onPressSaveButton={
              isFind
                ? isFoundMode
                  ? handleUseSelectedPinPress
                  : null
                : undefined
            }
            saveButtonText="Done"
            cancelButtonText="Close"
          />
        )
      }
    >
      <View
        onLayout={handleOnLayout}
        style={{
          flex: 1,
        }}
      >
        {!!canvasDims.height && (
          <ViewZoom
            ref={zoomableViewRef}
            onScaleChange={handleCanvasZoomChange}
            onPan={handleCanvasPan}
            onSingleTap={handleAddPin}
            animate={false}
            contentsSize={{
              width: calculatedSize.imageWidth,
              height: calculatedSize.imageHeight,
            }}
          >
            <View
              style={[styles.imageInnerContainer, { pointerEvents: 'none' }]}
            >
              <CanvasImage
                ref={canvasImageRef}
                base64={image.base64}
                url={image.url}
                width={calculatedSize.imageWidth}
                height={calculatedSize.imageHeight}
                page={image.page}
                type={image.type}
              />
            </View>
          </ViewZoom>
        )}
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          pointerEvents="box-none"
        >
          <PinsOther
            ref={pinsOtherRef}
            width={calculatedSize.imageWidth}
            height={calculatedSize.imageHeight}
            scale={zoomScale.current}
            onPinPress={isFind ? handlePinPress : undefined}
          />
          <PinsMain
            ref={pinsRef}
            width={calculatedSize.imageWidth}
            height={calculatedSize.imageHeight}
            scale={zoomScale.current}
            onPinMove={isCreate ? handlePinMove : undefined}
            onPinPress={handleMainPinPress}
            onPinPressUp={handleMainPinPressUp}
            image={image}
          />
        </View>
        <Pressable
          style={{
            position: 'absolute',
            right: theme.paddingHorizontalHalf,
            top: 10 - 6,
            zIndex: 999,
            flexDirection: 'column',
            alignItems: 'center',
            padding: 7,
          }}
        >
          {(isCreate || isFind) && (
            <Button
              icon={ICONS.ListLight}
              onPress={() =>
                dispatch(
                  NAVIGATE_MODAL({ screen: ModalScreens.PLANLIST, params: {} }),
                )
              }
              style={[
                styles.btnZoom,
                {
                  opacity: 0.9,
                  shadowColor: theme.colors.black,
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0.5,
                  shadowRadius: 2,
                },
              ]}
            />
          )}
          <Button
            icon={ICONS.CircleInfoLight}
            onPress={() =>
              dispatch(
                NAVIGATE_MODAL({ screen: ModalScreens.PLANINFO, params: {} }),
              )
            }
            style={[
              styles.btnZoom,
              {
                opacity: 0.9,
                marginTop: theme.paddingHorizontalHalf,
                shadowColor: theme.colors.black,
                shadowOffset: { width: 0, height: 0 },
                shadowOpacity: 0.5,
                shadowRadius: 2,
              },
            ]}
          />
          <ViewWithSeparators
            style={{
              opacity: 0.9,
              borderRadius: 4,
              marginTop: theme.paddingHorizontalHalf,
              shadowColor: theme.colors.black,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 2,
            }}
            separatorColor={theme.colors.background80}
          >
            <Button
              icon={ICONS.PlusLight}
              onPress={handleZoomIn}
              style={[
                styles.btnZoom,
                {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              ]}
            />
            <Button
              icon={ICONS.CircleDotSolid}
              iconSize={12}
              onPress={handleZoomReset}
              style={[
                styles.btnZoom,
                {
                  borderRadius: 0,
                  justifyContent: 'center',
                },
              ]}
            />
            <Button
              icon={ICONS.MinusLight}
              onPress={handleZoomOut}
              style={[
                styles.btnZoom,
                { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
              ]}
            />
          </ViewWithSeparators>
        </Pressable>
        {showRemovePinBtn && (
          <View
            style={{
              position: 'absolute',
              zIndex: 1,
              bottom: theme.paddingVertical,
              alignSelf: 'center',
              flexDirection: 'row',
            }}
          >
            <Button
              text="Delete Pin"
              onPress={() => dispatch(doPlanUnsetPin())}
              buttonTheme={ButtonTheme.secondary}
              destructive
              style={{ opacity: 0.9 }}
            />
            {!!showGoToPlanId && (
              <Button
                text={`Go To Pin${str.apos}s Plan`}
                onPress={() =>
                  dispatch(doPlansLoadPlan({ id: showGoToPlanId }))
                }
                buttonTheme={ButtonTheme.secondary}
                style={{ opacity: 0.9, marginLeft: theme.paddingHorizontal }}
              />
            )}
          </View>
        )}
      </View>
    </Page>
  );
};

const styles = StyleSheet.create({
  imageInnerContainer: {
    backgroundColor: 'white',
    shadowColor: 'black',
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 50,
  },
  btnZoom: {
    height: BUTTON_HEIGHT + 4,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  },
});
